var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Handle, Position } from "@xyflow/react";
import { Box, Typography, Select, Option, Stack, Grid, Chip } from "@mui/joy";
import { useState, useEffect, useMemo, memo } from "react";
import TimePicker from "react-time-picker";
import { getTimeZones } from "@vvo/tzdb";
import "../../../../../styles/timePickerStyles.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
var DAYS_OF_WEEK = [
    { value: "Monday", label: "Mon" },
    { value: "Tuesday", label: "Tue" },
    { value: "Wednesday", label: "Wed" },
    { value: "Thursday", label: "Thu" },
    { value: "Friday", label: "Fri" },
    { value: "Saturday", label: "Sat" },
    { value: "Sunday", label: "Sun" },
];
// Common timezones for better performance
var COMMON_TIMEZONES = [
    "America/Los_Angeles",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "Europe/London",
    "Europe/Paris",
    "Asia/Tokyo",
    "Australia/Sydney",
    "UTC"
];
var TimeRouterNode = memo(function TimeRouterNode(_a) {
    var data = _a.data, isConnectable = _a.isConnectable;
    var _b = useState(data.startTime || "09:00"), startTime = _b[0], setStartTime = _b[1];
    var _c = useState(data.endTime || "17:00"), endTime = _c[0], setEndTime = _c[1];
    var _d = useState(data.timeZone || "America/Los_Angeles"), selectedTimeZone = _d[0], setSelectedTimeZone = _d[1];
    var _e = useState(false), showAllTimezones = _e[0], setShowAllTimezones = _e[1];
    var _f = useState(data.selectedDays || []), selectedDays = _f[0], setSelectedDays = _f[1];
    var timeZones = useMemo(function () {
        if (!showAllTimezones) {
            return COMMON_TIMEZONES.map(function (name) { return ({ name: name }); });
        }
        return getTimeZones();
    }, [showAllTimezones]);
    useEffect(function () {
        data.startTime = (startTime === null || startTime === void 0 ? void 0 : startTime.toString()) || "09:00";
        data.endTime = (endTime === null || endTime === void 0 ? void 0 : endTime.toString()) || "17:00";
        data.timeZone = selectedTimeZone;
        data.selectedDays = selectedDays;
    }, [startTime, endTime, selectedTimeZone, selectedDays, data]);
    var handleTimeChange = function (type, value) {
        if (type === "start") {
            setStartTime(value);
        }
        else {
            setEndTime(value);
        }
    };
    var toggleDay = function (day) {
        setSelectedDays(function (prev) {
            return prev.includes(day)
                ? prev.filter(function (d) { return d !== day; })
                : __spreadArray(__spreadArray([], prev, true), [day], false);
        });
    };
    return (_jsxs(Box, __assign({ sx: {
            padding: 1.5,
            borderRadius: 1.5,
            backgroundColor: "background.level1",
            border: "1px solid",
            borderColor: "divider",
            minWidth: 250,
        } }, { children: [_jsx(Handle, { type: "target", position: Position.Top, isConnectable: isConnectable }), _jsxs(Stack, __assign({ spacing: 1 }, { children: [_jsx(Typography, __assign({ level: "title-sm", sx: { textAlign: "center", mb: 0.5 } }, { children: "Time Router" })), _jsxs(Grid, __assign({ container: true, spacing: 1 }, { children: [_jsx(Grid, __assign({ xs: 6 }, { children: _jsxs(Stack, __assign({ spacing: 0.5 }, { children: [_jsx(Typography, __assign({ level: "body-xs" }, { children: "Start" })), _jsx(TimePicker, { onChange: function (value) { return handleTimeChange("start", value); }, value: startTime, disableClock: true, clearIcon: null, clockIcon: null, className: "custom-time-picker" })] })) })), _jsx(Grid, __assign({ xs: 6 }, { children: _jsxs(Stack, __assign({ spacing: 0.5 }, { children: [_jsx(Typography, __assign({ level: "body-xs" }, { children: "End" })), _jsx(TimePicker, { onChange: function (value) { return handleTimeChange("end", value); }, value: endTime, disableClock: true, clearIcon: null, clockIcon: null, className: "custom-time-picker" })] })) }))] })), _jsxs(Stack, __assign({ spacing: 0.5 }, { children: [_jsx(Typography, __assign({ level: "body-xs" }, { children: "Time Zone" })), _jsx(Select, __assign({ value: selectedTimeZone, onChange: function (_, value) { return value && setSelectedTimeZone(value); }, size: "sm" }, { children: timeZones.map(function (tz) { return (_jsx(Option, __assign({ value: tz.name }, { children: tz.name }), tz.name)); }) })), !showAllTimezones && (_jsx(Typography, __assign({ level: "body-xs", sx: {
                                    cursor: 'pointer',
                                    color: 'primary.500',
                                    textAlign: 'right',
                                    mt: 0.5
                                }, onClick: function () { return setShowAllTimezones(true); } }, { children: "Show all timezones" })))] })), _jsx(Stack, __assign({ spacing: 0.5 }, { children: _jsx(Box, __assign({ sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 } }, { children: DAYS_OF_WEEK.map(function (day) { return (_jsx(Chip, __assign({ size: "sm", variant: selectedDays.includes(day.value) ? "solid" : "outlined", color: selectedDays.includes(day.value) ? "primary" : "neutral", onClick: function () { return toggleDay(day.value); }, sx: { cursor: 'pointer' } }, { children: day.label }), day.value)); }) })) }))] })), _jsx(Handle, { type: "source", position: Position.Bottom, isConnectable: isConnectable })] })));
});
export default TimeRouterNode;
